import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Lock, 
  Eye, 
  EyeOff, 
  BarChart3, 
  DollarSign, 
  Zap, 
  Clock,
  TrendingUp,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  User,
  Link,
  ChevronDown,
  ChevronUp,
  ShieldAlert,
  MessageSquare
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminPage() {
  const navigate = useNavigate();
  const { token, isAuthenticated: authIsAuthenticated } = useAuth();
  const [isAdmin, setIsAdmin] = useState(null); // null = checking
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Usage data states
  const [summary, setSummary] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [featureStats, setFeatureStats] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [userStats, setUserStats] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [selectedDays, setSelectedDays] = useState(30);
  const [expandedRows, setExpandedRows] = useState({});

  // Check for admin user or existing admin token
  useEffect(() => {
    const checkAdminAccess = async () => {
      // First check if user is logged in and is admin
      if (token) {
        try {
          const response = await axios.get(`${API_BASE}/api/auth/admin-check`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.is_admin) {
            setIsAdmin(true);
            setIsAuthenticated(true);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.log('Admin check failed:', error);
        }
      }
      
      // Fallback to admin token
      const adminToken = localStorage.getItem('admin_token');
      if (adminToken) {
        setIsAuthenticated(true);
      }
      setIsAdmin(false);
      setLoading(false);
    };
    
    checkAdminAccess();
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');
    
    try {
      const response = await axios.post(`${API_BASE}/api/admin/login`, {
        password
      });
      
      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token);
        setIsAuthenticated(true);
      }
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setSummary(null);
    setDailyStats([]);
    setFeatureStats([]);
    setRecentRequests([]);
  };

  const getAuthHeaders = useCallback(() => {
    // If user is admin, use their token
    if (isAdmin && token) {
      return { Authorization: `Bearer ${token}` };
    }
    // Otherwise use admin secret
    const adminToken = localStorage.getItem('admin_token');
    return { 'x-admin-secret': adminToken };
  }, [isAdmin, token]);

  const fetchUsageData = useCallback(async () => {
    setDataLoading(true);
    setDataError(null);
    
    try {
      const headers = getAuthHeaders();
      
      const [summaryRes, dailyRes, featureRes, recentRes, userRes] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/usage/summary?days=${selectedDays}`, { headers }),
        axios.get(`${API_BASE}/api/admin/usage/daily?days=${selectedDays}`, { headers }),
        axios.get(`${API_BASE}/api/admin/usage/by-feature?days=${selectedDays}`, { headers }),
        axios.get(`${API_BASE}/api/admin/usage/recent?limit=20`, { headers }),
        axios.get(`${API_BASE}/api/admin/usage/by-user?days=${selectedDays}`, { headers })
      ]);
      
      setSummary(summaryRes.data);
      setDailyStats(dailyRes.data.data || []);
      setFeatureStats(featureRes.data.data || []);
      setRecentRequests(recentRes.data.data || []);
      setUserStats(userRes.data.data || []);
    } catch (error) {
      if (error.response?.status === 401) {
        if (!isAdmin) {
          handleLogout();
          setLoginError('Session expired. Please login again.');
        }
      } else {
        setDataError(error.response?.data?.detail || 'Failed to fetch usage data');
      }
    } finally {
      setDataLoading(false);
    }
  }, [selectedDays, getAuthHeaders, isAdmin]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUsageData();
    }
  }, [isAuthenticated, fetchUsageData]);

  const formatCost = (cost) => {
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    if (cost < 1) return `$${cost.toFixed(3)}`;
    return `$${cost.toFixed(2)}`;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const toggleRowExpanded = (idx) => {
    setExpandedRows(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const truncateUrl = (url, maxLen = 40) => {
    if (!url) return '-';
    if (url.length <= maxLen) return url;
    return url.substring(0, maxLen) + '...';
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#09090b' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  // Login Form - only show if not admin user and not authenticated via token
  if (!isAuthenticated) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: '#09090b' }}
      >
        <div 
          className="w-full max-w-md p-8 rounded-2xl"
          style={{ 
            backgroundColor: 'rgba(255,255,255,0.02)', 
            border: '1px solid rgba(255,255,255,0.05)' 
          }}
        >
          <div className="text-center mb-8">
            <div 
              className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
            >
              <Lock className="h-8 w-8" style={{ color: '#ef4444' }} />
            </div>
            <h1 className="text-2xl font-bold" style={{ color: '#e4e4e7' }}>Admin Access</h1>
            <p className="text-sm mt-2" style={{ color: '#71717a' }}>
              This page is restricted to administrators only
            </p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm mb-2" style={{ color: '#a1a1aa' }}>
                Admin Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg text-sm outline-none"
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#e4e4e7'
                  }}
                  placeholder="Enter admin password"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: '#71717a' }}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {loginError && (
              <div 
                className="flex items-center gap-2 p-3 rounded-lg text-sm"
                style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#f87171' }}
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {loginError}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading || !password}
              className="w-full py-3 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              style={{ 
                backgroundColor: '#ef4444',
                color: 'white'
              }}
            >
              {loading ? 'Authenticating...' : 'Access Admin Panel'}
            </button>
          </form>
          
          <button
            onClick={() => navigate('/')}
            className="w-full mt-4 py-2 text-sm"
            style={{ color: '#71717a' }}
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div 
      className="min-h-screen p-6"
      style={{ backgroundColor: '#09090b' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#e4e4e7' }}>
              Admin Dashboard
            </h1>
            <p className="text-sm mt-1" style={{ color: '#71717a' }}>
              LLM API Usage & Cost Monitoring
              {isAdmin && <span className="ml-2 text-green-400">(Admin User)</span>}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Quick Links */}
            <button
              onClick={() => navigate('/admin/sims')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors"
              style={{ 
                backgroundColor: 'rgba(168, 85, 247, 0.1)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                color: '#a855f7'
              }}
            >
              <MessageSquare className="h-4 w-4" />
              Support Tickets
            </button>
            
            <button
              onClick={() => navigate('/admin/returns')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors"
              style={{ 
                backgroundColor: 'rgba(96, 165, 250, 0.1)',
                border: '1px solid rgba(96, 165, 250, 0.3)',
                color: '#60a5fa'
              }}
            >
              <TrendingUp className="h-4 w-4" />
              Return Tracking
            </button>
            
            <select
              value={selectedDays}
              onChange={(e) => setSelectedDays(parseInt(e.target.value))}
              className="px-3 py-2 rounded-lg text-sm outline-none"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#e4e4e7'
              }}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
            
            <button
              onClick={fetchUsageData}
              disabled={dataLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#e4e4e7'
              }}
            >
              <RefreshCw className={`h-4 w-4 ${dataLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            {!isAdmin && (
              <button
                onClick={handleLogout}
                className="px-4 py-2 rounded-lg text-sm"
                style={{ 
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: '#f87171'
                }}
              >
                Logout
              </button>
            )}
          </div>
        </div>
        
        {dataError && (
          <div 
            className="mb-6 p-4 rounded-lg flex items-center gap-2"
            style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#f87171' }}
          >
            <AlertCircle className="h-5 w-5" />
            {dataError}
          </div>
        )}
        
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div 
              className="p-5 rounded-xl"
              style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
                  <DollarSign className="h-5 w-5" style={{ color: '#22c55e' }} />
                </div>
                <span className="text-sm" style={{ color: '#71717a' }}>Total Cost</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: '#e4e4e7' }}>
                {formatCost(summary.total_cost_usd || 0)}
              </p>
              <p className="text-xs mt-1" style={{ color: '#52525b' }}>
                Last {summary.period_days} days
              </p>
            </div>
            
            <div 
              className="p-5 rounded-xl"
              style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(96, 165, 250, 0.1)' }}>
                  <Zap className="h-5 w-5" style={{ color: '#60a5fa' }} />
                </div>
                <span className="text-sm" style={{ color: '#71717a' }}>Total Requests</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: '#e4e4e7' }}>
                {formatNumber(summary.total_requests || 0)}
              </p>
              <p className="text-xs mt-1" style={{ color: '#52525b' }}>
                {summary.successful_requests || 0} successful
              </p>
            </div>
            
            <div 
              className="p-5 rounded-xl"
              style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)' }}>
                  <BarChart3 className="h-5 w-5" style={{ color: '#fbbf24' }} />
                </div>
                <span className="text-sm" style={{ color: '#71717a' }}>Total Tokens</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: '#e4e4e7' }}>
                {formatNumber((summary.total_input_tokens || 0) + (summary.total_output_tokens || 0))}
              </p>
              <p className="text-xs mt-1" style={{ color: '#52525b' }}>
                {formatNumber(summary.total_input_tokens || 0)} in / {formatNumber(summary.total_output_tokens || 0)} out
              </p>
            </div>
            
            <div 
              className="p-5 rounded-xl"
              style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(168, 85, 247, 0.1)' }}>
                  <TrendingUp className="h-5 w-5" style={{ color: '#a855f7' }} />
                </div>
                <span className="text-sm" style={{ color: '#71717a' }}>Avg Cost/Request</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: '#e4e4e7' }}>
                {formatCost(summary.avg_cost_per_request || 0)}
              </p>
              <p className="text-xs mt-1" style={{ color: '#52525b' }}>
                per analysis
              </p>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Usage by Feature */}
          <div 
            className="p-5 rounded-xl"
            style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
          >
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#e4e4e7' }}>
              Usage by Feature
            </h3>
            {featureStats.length > 0 ? (
              <div className="space-y-3">
                {featureStats.map((stat, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 rounded-lg"
                    style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}
                  >
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#e4e4e7' }}>
                        {stat.feature?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                      </p>
                      <p className="text-xs" style={{ color: '#71717a' }}>
                        {stat.requests} requests • {formatNumber(stat.input_tokens + stat.output_tokens)} tokens
                      </p>
                    </div>
                    <p className="text-sm font-semibold" style={{ color: '#22c55e' }}>
                      {formatCost(stat.cost_usd)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm" style={{ color: '#71717a' }}>No usage data available</p>
            )}
          </div>
          
          {/* Usage by User */}
          <div 
            className="p-5 rounded-xl"
            style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <User className="h-5 w-5" style={{ color: '#60a5fa' }} />
              <h3 className="text-lg font-semibold" style={{ color: '#e4e4e7' }}>
                Usage by User
              </h3>
            </div>
            {userStats.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {userStats.map((stat, idx) => (
                  <div 
                    key={idx}
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium" style={{ color: '#e4e4e7' }}>
                          {stat.user_email || 'anonymous'}
                        </p>
                        <p className="text-xs" style={{ color: '#71717a' }}>
                          {stat.requests} requests • {formatNumber(stat.total_tokens)} tokens
                        </p>
                      </div>
                      <p className="text-sm font-semibold" style={{ color: '#22c55e' }}>
                        {formatCost(stat.cost_usd)}
                      </p>
                    </div>
                    {stat.last_request && (
                      <p className="text-xs mt-1" style={{ color: '#52525b' }}>
                        Last: {new Date(stat.last_request).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm" style={{ color: '#71717a' }}>No user data available</p>
            )}
          </div>
        </div>
        
        {/* Daily Stats */}
        <div 
          className="p-5 rounded-xl mb-8"
          style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: '#e4e4e7' }}>
            Daily Usage
          </h3>
          {dailyStats.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              {dailyStats.slice(0, 14).map((stat, idx) => (
                <div 
                  key={idx}
                  className="p-3 rounded-lg text-center"
                  style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}
                >
                  <p className="text-xs font-mono" style={{ color: '#71717a' }}>{stat.date.slice(5)}</p>
                  <p className="text-lg font-bold" style={{ color: '#e4e4e7' }}>{stat.requests}</p>
                  <p className="text-xs" style={{ color: '#22c55e' }}>{formatCost(stat.cost_usd)}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm" style={{ color: '#71717a' }}>No daily data available</p>
          )}
        </div>
        
        {/* Recent Requests */}
        <div 
          className="p-5 rounded-xl"
          style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: '#e4e4e7' }}>
            Recent Requests
          </h3>
          {recentRequests.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <th className="text-left py-2 px-3" style={{ color: '#71717a' }}>Time</th>
                    <th className="text-left py-2 px-3" style={{ color: '#71717a' }}>User</th>
                    <th className="text-left py-2 px-3" style={{ color: '#71717a' }}>Feature</th>
                    <th className="text-left py-2 px-3" style={{ color: '#71717a' }}>URL/Ticker</th>
                    <th className="text-right py-2 px-3" style={{ color: '#71717a' }}>Tokens</th>
                    <th className="text-right py-2 px-3" style={{ color: '#71717a' }}>Cost</th>
                    <th className="text-center py-2 px-3" style={{ color: '#71717a' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRequests.map((req, idx) => (
                    <React.Fragment key={idx}>
                      <tr 
                        className="cursor-pointer hover:bg-white/5"
                        onClick={() => toggleRowExpanded(idx)}
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}
                      >
                        <td className="py-2 px-3 font-mono text-xs" style={{ color: '#71717a' }}>
                          {new Date(req.timestamp).toLocaleString()}
                        </td>
                        <td className="py-2 px-3" style={{ color: '#60a5fa' }}>
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            <span className="text-xs">{req.user_email || 'anonymous'}</span>
                          </div>
                        </td>
                        <td className="py-2 px-3" style={{ color: '#e4e4e7' }}>
                          {req.feature?.replace(/_/g, ' ') || 'unknown'}
                        </td>
                        <td className="py-2 px-3" style={{ color: '#a1a1aa' }}>
                          <div className="flex items-center gap-1">
                            {req.metadata?.url ? (
                              <>
                                <Link className="h-3 w-3 flex-shrink-0" />
                                <span className="text-xs truncate max-w-[150px]" title={req.metadata.url}>
                                  {truncateUrl(req.metadata.url, 25)}
                                </span>
                              </>
                            ) : req.metadata?.ticker ? (
                              <span className="text-xs font-mono">{req.metadata.ticker}</span>
                            ) : (
                              <span className="text-xs">-</span>
                            )}
                            {(req.metadata?.url || req.metadata?.ticker) && (
                              expandedRows[idx] ? 
                                <ChevronUp className="h-3 w-3 ml-1" /> : 
                                <ChevronDown className="h-3 w-3 ml-1" />
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-right" style={{ color: '#a1a1aa' }}>
                          {formatNumber(req.total_tokens || 0)}
                        </td>
                        <td className="py-2 px-3 text-right" style={{ color: '#22c55e' }}>
                          {formatCost(req.cost_usd || 0)}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {req.success ? (
                            <CheckCircle className="h-4 w-4 inline" style={{ color: '#22c55e' }} />
                          ) : (
                            <AlertCircle className="h-4 w-4 inline" style={{ color: '#ef4444' }} />
                          )}
                        </td>
                      </tr>
                      {expandedRows[idx] && req.metadata && (
                        <tr style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                          <td colSpan="7" className="py-3 px-4">
                            <div className="text-xs space-y-1" style={{ color: '#a1a1aa' }}>
                              {req.metadata.url && (
                                <div className="flex items-start gap-2">
                                  <span style={{ color: '#71717a' }}>URL:</span>
                                  <a 
                                    href={req.metadata.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="break-all hover:underline"
                                    style={{ color: '#60a5fa' }}
                                  >
                                    {req.metadata.url}
                                  </a>
                                </div>
                              )}
                              {req.metadata.ticker && (
                                <div>
                                  <span style={{ color: '#71717a' }}>Ticker:</span> <span className="font-mono">{req.metadata.ticker}</span>
                                </div>
                              )}
                              <div>
                                <span style={{ color: '#71717a' }}>Model:</span> <span className="font-mono">{req.model}</span>
                              </div>
                              <div>
                                <span style={{ color: '#71717a' }}>Tokens:</span> {req.input_tokens} in / {req.output_tokens} out
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm" style={{ color: '#71717a' }}>No recent requests</p>
          )}
        </div>
      </div>
    </div>
  );
}
